import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {ResizeObserver|null} */
  #resizeObserver = null;

  /** @type {MutationObserver|null} */
  #mutationObserver = null;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(
      CartAddEvent.eventName,
      this.#handleCartAdd
    );

    document.addEventListener(
      'cart:update',
      this.#handleCartUpdate
    );

    this.addEventListener(
      DialogOpenEvent.eventName,
      this.#handleDialogOpen
    );

    window.addEventListener(
      'resize',
      this.#scheduleStickyUpdate
    );

    /*
     * Safari can delay layout after dynamically replacing
     * cart drawer content. Watch the drawer itself for changes.
     */
    requestAnimationFrame(() => {
      this.#startObservers();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      CartAddEvent.eventName,
      this.#handleCartAdd
    );

    document.removeEventListener(
      'cart:update',
      this.#handleCartUpdate
    );

    this.removeEventListener(
      DialogOpenEvent.eventName,
      this.#handleDialogOpen
    );

    window.removeEventListener(
      'resize',
      this.#scheduleStickyUpdate
    );

    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
      this.#resizeObserver = null;
    }

    if (this.#mutationObserver) {
      this.#mutationObserver.disconnect();
      this.#mutationObserver = null;
    }
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    this.#scheduleStickyUpdate();
  };

  #handleCartUpdate = () => {
    this.#scheduleStickyUpdate();

    /*
     * Dynamic cart sections may have been replaced,
     * so reconnect observers to the new elements.
     */
    setTimeout(() => {
      this.#startObservers();
      this.#scheduleStickyUpdate();
    }, 100);
  };

  #handleDialogOpen = () => {
    this.#startObservers();
    this.#scheduleStickyUpdate();
  };

  #startObservers() {
    const { dialog } = /** @type {Refs} */ (this.refs);

    if (!dialog) return;

    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
    }

    this.#resizeObserver = new ResizeObserver(() => {
      this.#scheduleStickyUpdate();
    });

    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (content) {
      this.#resizeObserver.observe(content);
    }

    if (summary) {
      this.#resizeObserver.observe(summary);
    }

    if (this.#mutationObserver) {
      this.#mutationObserver.disconnect();
    }

    this.#mutationObserver = new MutationObserver(() => {
      /*
       * Force Safari to flush the new layout.
       */
      void dialog.offsetHeight;

      this.#scheduleStickyUpdate();
    });

    this.#mutationObserver.observe(dialog, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: [
        'open',
        'hidden',
        'style',
        'class'
      ]
    });
  }

  #scheduleStickyUpdate = () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const { dialog } = /** @type {Refs} */ (this.refs);

        if (dialog) {
          /*
           * Reading offsetHeight forces Safari to calculate
           * the latest layout before we measure anything.
           */
          void dialog.offsetHeight;
        }

        this.#updateStickyState();
      });
    });
  };

  open() {
    this.showDialog();

    this.#startObservers();
    this.#scheduleStickyUpdate();

    /**
     * Close cart drawer when installments CTA is clicked
     * to avoid overlapping dialogs.
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent =
        document.querySelector('shopify-payment-terms')?.shadowRoot;

      const cta =
        installmentsContent?.querySelector('#shopify-installments-cta');

      cta?.addEventListener(
        'click',
        this.closeDialog,
        { once: true }
      );
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);

    if (!dialog) return;

    const content =
      dialog.querySelector('.cart-drawer__content');

    const summary =
      dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      dialog.setAttribute(
        'cart-summary-sticky',
        'false'
      );

      return;
    }

    const drawerHeight =
      dialog.getBoundingClientRect().height;

    const summaryHeight =
      summary.getBoundingClientRect().height;

    if (!drawerHeight) return;

    const ratio =
      summaryHeight / drawerHeight;

    dialog.setAttribute(
      'cart-summary-sticky',
      ratio > this.#summaryThreshold
        ? 'false'
        : 'true'
    );
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define(
    'cart-drawer-component',
    CartDrawerComponent
  );
}